import { Box, Typography } from '@mui/material'
import { ApiPlayers } from 'api'
import { format } from 'date-fns'
import {
  MaterialReactTable,
  MRT_ColumnFiltersState,
  MRT_RowVirtualizer,
  useMaterialReactTable,
} from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInfiniteQuery } from 'react-query'
import mrtLocalization from '../../constants/mrtLocalization'
import { EDateFormat } from '../../utils/date'
import { createColumns } from './columns'
import { FinanceSummary } from './Summary'
import { FinanceTableToolbar, useDateRange } from './Toolbar'
import { IFinanceQuery, PlayerFinancialHistoryProps } from './types'

const PlayerFinancialHistoryTableRefactored: FC<PlayerFinancialHistoryProps> = observer((props) => {
  const { id: playerId, login, currency } = props
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null)
  const { range, setRange } = useDateRange()
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])

  const query: IFinanceQuery = useMemo(() => {
    const reasons = columnFilters.find((item) => item.id === 'reason')?.value as string[] | undefined

    return {
      ...(range.from &&
        range.to && {
          from: format(range.from, EDateFormat.yyy_MM_dd_HH_mm),
          to: format(range.to, EDateFormat.yyy_MM_dd_HH_mm),
        }),
      limit: 50,
      offset: 0,
      reasons,
    }
  }, [range, columnFilters])

  const { data, isError, isLoading, isFetching, refetch, fetchNextPage } = useInfiniteQuery(
    ['finances', playerId, query],
    async ({ pageParam = 0 }) => {
      return ApiPlayers.getFinanceByPlayerId({
        playerId,
        ...query,
        offset: pageParam,
      })
    },
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalFetched = allPages.flatMap((page) => page.rows).length
        return totalFetched < lastPage.total ? totalFetched : undefined
      },
    }
  )

  const flatData = useMemo(() => data?.pages.flatMap((page) => page.rows) ?? [], [data])

  const totalDBRowCount = data?.pages?.[0]?.total ?? 0
  const totalFetched = flatData.length
  const totalGGR = data?.pages?.[0]?.totalGGR ?? 0

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement
        // once the user has scrolled within 400px of the bottom of the table, fetch more data if we can
        if (scrollHeight - scrollTop - clientHeight < 400 && !isFetching && totalFetched < totalDBRowCount) {
          fetchNextPage()
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  )

  // Сброс данных при изменении фильтров/сортировки/дат
  useEffect(() => {
    rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
  }, [columnFilters, range])

  // Колонки таблицы
  const columns = useMemo(() => createColumns(currency), [currency])

  // Обработчики для toolbar
  const handleResetFilters = () => {
    setColumnFilters([])
  }

  const table = useMaterialReactTable({
    ...mrtLocalization,
    columns,
    data: flatData,
    enableRowVirtualization: true,
    rowVirtualizerOptions: {
      overscan: 5,
    },
    rowVirtualizerInstanceRef,
    enableFullScreenToggle: true,
    enablePagination: false,
    enableGlobalFilter: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnActions: true,
    enableColumnFilters: true,
    enableSorting: false,
    enableDensityToggle: false,
    onColumnFiltersChange: setColumnFilters,
    manualFiltering: true,
    manualSorting: true,
    enableSortingRemoval: false,
    state: {
      columnFilters,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
      showColumnFilters: true,
      density: 'compact',
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: { maxHeight: '600px' },
      onScroll: (event: React.UIEvent<HTMLDivElement>) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
    },
    renderBottomToolbarCustomActions: () => (
      <FinanceSummary
        totalRecords={totalDBRowCount}
        loadedRecords={totalFetched}
        totalAmount={totalGGR}
        currency={currency}
        dateRange={{
          from: query.from,
          to: query.to,
        }}
      />
    ),
    renderTopToolbarCustomActions: () => (
      <FinanceTableToolbar
        playerId={playerId}
        playerLogin={login}
        query={query}
        range={range}
        onRangeChange={setRange}
        onApply={refetch}
        onResetFilters={handleResetFilters}
      />
    ),
  })

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Финансовая история игрока: {login}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  )
})

export default PlayerFinancialHistoryTableRefactored
